/**
 * 评测批次报告（PR9 新写；模拟场景「报告」页签与评测记录的批次抽屉共用，判定卡 05 差异 #5、#8、#9，ref 04 O-8、O-10）。
 *
 * - 批次进度块：排队 ／ 运行中（已完成 x/y · 已花费 · 当前题 · [停批]）／ 停止中 ／ 中断 ／ 已停 ／ 完成，有未判定时给「补跑未判定」；
 * - 报告体：批次头（计划来源、配置快照号、题库快照号）→ 指标卡一行（通过率 · action · nl · 有效样本 · 未判定 · 写对象错误 · 没办成说办成）
 *   → pass^k → 按族 → 情绪与风险（观测，10-22：情绪准确率、各风险旗召回与误报、反例题误报；没判过显示「未判分」）→ 按题块（每题每次一行；「未判定」单列一种结果、不计入通过；有效次数不够规定次数标「次数不足」）；
 * - 点一次试跑：运行轨迹抽屉（单元 3）＋ 顶部评分卡条；判官逐条结论带 AI 标记。
 * - 盲测题（`sealed`，10-21 A1）：题块带「盲测」徽标，结果行只有结论与聚合数；点开只显示结论、轮数、花费和「盲测题，内容不展示」，
 *   不开轨迹（接口层已不给对话、评分卡、运行记录号）。
 */
import { type ReactNode, useEffect, useRef, useState } from "react";

import { AiMark } from "@/components/ui/ai-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { KpiCard } from "@/components/ui/stats";
import {
	BATCH_ACTIVE,
	type PlanBatchReport,
	type PlanEmotionRiskFlag,
	type PlanEmotionRiskSummary,
	type PlanRunEnv,
	type PlanTrialDetail,
	type PlanTrialResult,
	planErrorMessage,
	useBatchProgress,
	usePlanBatchReport,
	usePlanTrial,
	useRerunBatch,
	useStopBatch,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { MetricTip } from "../kit/metric-tip";
import {
	BATCH_STATUS_TONE,
	BATCH_STATUS_ZH,
	BATCH_STOP_ZH,
	OUTCOME_TONE,
	OUTCOME_ZH,
	PERSONA_ZH,
	STABLE_TONE,
	STABLE_ZH,
	TERMINATION_ZH,
	UNDETERMINED_ZH,
	zh,
} from "../kit/plan-labels";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { SealedNotice } from "../kit/sealed-notice";
import { BankTag, SnapshotTag } from "../kit/snapshot-tag";
import { PlanTraceDrawer } from "../trace-drawer/plan-trace-drawer";

export const pctText = (v: number | null | undefined) =>
	v == null ? "—" : `${Math.round(v * 100)}%`;
const usd = (v: number | null | undefined) => (v == null ? "—" : `$${v.toFixed(2)}`);
const secs = (v: number | null | undefined) =>
	v == null
		? "—"
		: v >= 60
			? `${Math.floor(v / 60)} 分 ${Math.round(v % 60)} 秒`
			: `${Math.round(v)} 秒`;

function DimMark({ label, v }: { label: string; v: boolean | null | undefined }) {
	if (v == null) return null;
	return (
		<Badge tone={v ? "success" : "danger"}>
			{label}
			{v ? " ✓" : " ✗"}
		</Badge>
	);
}

/** 一次试跑的结果徽标：过 ／ 败 ／ 未判定（原因） */
export function OutcomeBadge({ r }: { r: Pick<PlanTrialResult, "outcome" | "reason"> }) {
	const label = zh(OUTCOME_ZH, r.outcome);
	return (
		<Badge tone={OUTCOME_TONE[r.outcome] ?? "muted"}>
			{r.outcome === "undetermined" && r.reason
				? `${label}（${zh(UNDETERMINED_ZH, r.reason)}）`
				: label}
		</Badge>
	);
}

/* ───────────────────────────── 批次进度 ───────────────────────────── */

/** 额度用完时去哪补：按**这一批自己**跑在哪个提供方说（批次建批时记的 run_env.llm_provider；11-02 之前的批次没记，当时都走 openrouter）；
 * 批次信息还没取到就只说去提供方补足 */
export function quotaHint(provider?: string | null): string {
	if (provider === "deepseek") return "去 DeepSeek 官网充值后补跑";
	if (provider === "openrouter") return "去 OpenRouter 补足余额或重置 key 额度后补跑";
	return "去模型提供方补足余额后补跑";
}

export function BatchProgressBlock({
	batchId,
	provider,
}: {
	batchId: string;
	/** 本批自己的模型提供方；undefined ＝ 批次信息还没取到 */
	provider?: string | null;
}) {
	const q = useBatchProgress(batchId);
	const stop = useStopBatch();
	const rerun = useRerunBatch();
	const p = q.data;
	let body: ReactNode;
	if (q.isLoading) body = <LoadingInline text="读取批次进度…" />;
	else if (q.isError || !p)
		body = (
			<FailState
				title="没取到批次进度"
				reason={planErrorMessage(q.error)}
				onRetry={() => q.refetch()}
				retrying={q.isFetching}
				testId="plan-progress-fail"
			/>
		);
	else {
		const status = p.status ?? "";
		const active = BATCH_ACTIVE.has(status);
		const ended = !active;
		const canStop = status === "running" || status === "queued";
		const canRerun = ended && p.undetermined > 0;
		const why =
			status === "interrupted"
				? "部署重启打断了批次，没跑完的记未判定，可补跑"
				: status === "stopped" && p.stop_reason
					? `${zh(BATCH_STOP_ZH, p.stop_reason)}：没跑的记未判定${p.stop_reason === "quota" ? `；${quotaHint(provider)}` : ""}`
					: null;
		body = (
			<div className="space-y-3">
				<div className="flex flex-wrap items-center gap-2 text-sm" data-testid="plan-progress">
					<Badge tone={BATCH_STATUS_TONE[status] ?? "muted"}>{zh(BATCH_STATUS_ZH, status)}</Badge>
					<span className="text-text-primary">
						已完成 <b data-testid="plan-progress-done">{p.done}</b> / {p.total_trials}
					</span>
					<span className="text-text-muted">
						已花费 {usd(p.cost_usd_so_far)}（单批上限 {usd(p.cost_limit_usd)}）
					</span>
					{p.undetermined ? <Badge tone="warning">未判定 {p.undetermined}</Badge> : null}
					{status === "queued" && p.queue_position ? (
						<span className="text-text-muted">排在第 {p.queue_position} 位</span>
					) : null}
					{p.running_scenario_ids.length ? (
						<span className="text-text-muted">当前题 {p.running_scenario_ids.join("、")}</span>
					) : null}
					{active ? (
						<span className="text-text-muted">
							已跑 {secs(p.elapsed_s)}
							{p.eta_s != null ? ` · 预计还要 ${secs(p.eta_s)}` : ""}
						</span>
					) : null}
					<span className="ml-auto flex gap-2">
						{canStop ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={stop.isPending || p.stop_requested}
								onClick={() => stop.mutate(batchId)}
								data-testid="plan-batch-stop"
							>
								{p.stop_requested ? "停止中…" : "停批"}
							</Button>
						) : null}
						{ended ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={!canRerun || rerun.isPending}
								title={canRerun ? "只补跑未判定的试跑，结果追加进本批" : "本批没有未判定的试跑"}
								onClick={() => rerun.mutate(batchId)}
								data-testid="plan-batch-rerun"
							>
								补跑未判定
							</Button>
						) : null}
					</span>
				</div>
				{status === "stopping" ? (
					<div className="text-xs text-text-muted">当前在跑的会话跑完就停，没跑的记未判定。</div>
				) : null}
				{why ? <div className="text-xs text-text-secondary">{why}</div> : null}
				{stop.isError || rerun.isError ? (
					<div className="text-xs text-brand-danger">
						{planErrorMessage(stop.error ?? rerun.error)}
					</div>
				) : null}
			</div>
		);
	}
	return (
		<Card data-testid="plan-block-progress">
			<CardContent className="px-4 py-3">{body}</CardContent>
		</Card>
	);
}

/* ───────────────────────────── 报告体 ───────────────────────────── */

const FAMILY_COLUMNS: PlanColumn<PlanBatchReport["by_family"][number]>[] = [
	{ key: "family", title: "族", render: (f) => f.family },
	{ key: "n", title: "题数", align: "right", render: (f) => f.scenarios },
	{
		key: "sp",
		title: <MetricTip id="eval.stable_pass">稳定过</MetricTip>,
		align: "right",
		render: (f) => f.stable_pass,
	},
	{
		key: "fl",
		title: <MetricTip id="eval.flaky">摇摆</MetricTip>,
		align: "right",
		render: (f) => f.flaky,
	},
	{
		key: "sf",
		title: <MetricTip id="eval.stable_fail">稳定败</MetricTip>,
		align: "right",
		render: (f) => f.stable_fail,
	},
	{
		key: "in",
		title: <MetricTip id="eval.insufficient">次数不足</MetricTip>,
		align: "right",
		render: (f) => f.insufficient,
	},
	{
		key: "p1",
		title: <MetricTip id="eval.family_pass_1">pass^1</MetricTip>,
		align: "right",
		render: (f) => pctText(f.pass_1),
	},
	{
		key: "p3",
		title: <MetricTip id="eval.family_pass_3">pass^3</MetricTip>,
		align: "right",
		render: (f) => pctText(f.pass_3),
	},
];

type ErFlagRow = PlanEmotionRiskFlag & { key: string; name: string };

const ER_FLAG_COLUMNS: PlanColumn<ErFlagRow>[] = [
	{ key: "name", title: "风险旗", render: (f) => f.name },
	{ key: "should", title: "应报", align: "right", render: (f) => f.should },
	{ key: "hit", title: "命中", align: "right", render: (f) => f.hit },
	{
		key: "recall",
		title: <MetricTip id="eval.er_flag_recall">召回率</MetricTip>,
		align: "right",
		render: (f) => pctText(f.recall),
	},
	{ key: "should_not", title: "不该报", align: "right", render: (f) => f.should_not },
	{ key: "false", title: "误报", align: "right", render: (f) => f.false },
	{
		key: "false_rate",
		title: <MetricTip id="eval.er_flag_false_rate">误报率</MetricTip>,
		align: "right",
		render: (f) => pctText(f.false_rate),
	},
];

/** 情绪与风险（观测，10-22）：信号每轮输出对金标期望，不进通过率。老批次 ／ 本批没有带期望的题 → 未判分 */
function EmotionRiskBlock({ er }: { er?: PlanEmotionRiskSummary }) {
	const rows: ErFlagRow[] = Object.entries(er?.flags ?? {}).map(([key, f]) => ({
		...f,
		key,
		name: er?.names?.[key] ?? key,
	}));
	const s = er?.sentiment;
	const ses = er?.session;
	const neg = er?.negative;
	return (
		<div className="space-y-2" data-testid="plan-report-emotion-risk">
			<div className="flex flex-wrap items-baseline gap-2">
				<span className="text-sm font-semibold text-text-primary">情绪与风险（观测）</span>
				<span className="text-xs text-text-muted">信号每轮输出对金标期望，不进通过率</span>
			</div>
			{er?.trials ? (
				<>
					<div
						className="flex flex-wrap gap-x-4 gap-y-1 text-sm"
						data-testid="plan-report-er-summary"
					>
						<span>
							<MetricTip id="eval.er_sentiment">情绪准确率</MetricTip>{" "}
							<b className="text-text-primary">{pctText(s?.rate)}</b>
							<span className="text-xs text-text-muted">
								（{s?.hit ?? 0}/{s?.judged ?? 0} 轮）
							</span>
						</span>
						<span>
							<MetricTip id="eval.er_session_missed">整段漏报</MetricTip>{" "}
							<b className="text-text-primary">{ses?.missed ?? 0}</b>
							<span className="text-xs text-text-muted">/{ses?.should ?? 0}</span>
						</span>
						<span>
							<MetricTip id="eval.er_session_false">整段误报</MetricTip>{" "}
							<b className="text-text-primary">{ses?.false ?? 0}</b>
							<span className="text-xs text-text-muted">/{ses?.should_not ?? 0}</span>
						</span>
						<span>
							<MetricTip id="eval.er_negative">反例题误报</MetricTip>{" "}
							<b className="text-text-primary">{neg?.trials_false ?? 0}</b>
							<span className="text-xs text-text-muted">
								/{neg?.trials ?? 0} 次试跑（{neg?.false_flags ?? 0} 个旗）
							</span>
						</span>
						<span className="text-xs text-text-muted">
							{er.trials} 次试跑{er.unaligned ? ` · 未对上 ${er.unaligned} 处（不进分母）` : ""}
						</span>
					</div>
					<PlanTable
						rows={rows}
						rowKey={(f) => f.key}
						columns={ER_FLAG_COLUMNS}
						density="compact"
					/>
				</>
			) : (
				<div className="text-xs text-text-muted" data-testid="plan-report-er-empty">
					未判分（本批没有带情绪与风险期望的题）
				</div>
			)}
		</div>
	);
}

/** 本批跑在什么环境上（收尾 B1：建批时记下的画像版本、工具后端、Jev 锁定版本与分档线、业务包指纹）；老批次没有就不显示 */
function RunEnvText({ env }: { env?: PlanRunEnv }) {
	if (!env || !(env.profile_version || env.jev_model)) return null;
	const jev = env.jev_model
		? `Jev ${env.jev_model}${env.jev_seen && env.jev_seen !== env.jev_model ? `（实际 ${env.jev_seen}）` : ""}`
		: null;
	const parts = [
		env.profile_version ? `画像 ${env.profile_version}` : null,
		env.tool_backend ? `工具后端 ${env.tool_backend}` : null,
		jev,
		env.yes_at != null && env.no_at != null ? `分档线 ${env.yes_at}／${env.no_at}` : null,
		env.pack_name
			? `业务包 ${env.pack_name}${env.pack_params ? `（参数 ${env.pack_params}）` : ""}`
			: null,
		env.tag_dict_version ? `字典 ${env.tag_dict_version}` : null,
		env.llm_provider ? `模型提供方 ${env.llm_provider}` : null,
	].filter(Boolean);
	return (
		<span
			className="font-mono"
			data-testid="plan-report-run-env"
			title="建批时记下的运行环境（job.assets）"
		>
			{parts.join(" · ")}
		</span>
	);
}

/** 报告体（模拟场景「报告」与评测记录的批次抽屉共用） */
export function BatchReportBody({
	report,
	onDrill,
}: {
	report: PlanBatchReport;
	onDrill: (r: PlanTrialResult, scenarioId: string) => void;
}) {
	const h = report.header;
	const o = report.overview;
	const ins = report.credibility.insufficient_scenarios ?? [];
	const undet = Object.entries(report.credibility.undetermined_reasons ?? {});
	return (
		<div className="space-y-4" data-testid="plan-report">
			<div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
				<span className="font-mono text-text-secondary">{h.batch_id}</span>
				<Badge tone={BATCH_STATUS_TONE[h.status ?? ""] ?? "muted"}>
					{zh(BATCH_STATUS_ZH, h.status)}
				</Badge>
				<span className="inline-flex items-center gap-1">
					配置 <SnapshotTag id={h.locked_snapshot_id} />
				</span>
				<span className="inline-flex items-center gap-1">
					题库 <BankTag id={h.bank_id} />
				</span>
				{h.gold_version ? <span>金标 {h.gold_version}</span> : null}
				<span>判官 {report.credibility.judge_calibration}</span>
				<RunEnvText env={h.run_env} />
				{report.partial ? <Badge tone="info">跑批中，先出已完成部分</Badge> : null}
			</div>

			<div
				className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6"
				data-testid="plan-report-kpis"
			>
				<KpiCard
					label={<MetricTip id="eval.pass_rate">通过率</MetricTip>}
					value={pctText(o.pass_rate)}
					hint={`有效 ${o.n} 次，扣安全否决${o.reward_rate != null ? `；不扣 ${pctText(o.reward_rate)}` : ""}`}
				/>
				<KpiCard
					label={<MetricTip id="eval.action">action 通过率</MetricTip>}
					value={pctText(o.dim_pass.action)}
					hint={
						o.deterministic?.judged || o.deterministic?.unparsed ? (
							<span data-testid="plan-report-deterministic">
								{`确定性检查机判 ${o.deterministic?.judged ?? 0} 条、没过 ${o.deterministic?.failed ?? 0}；未机判 ${o.deterministic?.unparsed ?? 0}（EV-25）`}
							</span>
						) : undefined
					}
				/>
				<KpiCard
					label={<MetricTip id="eval.nl">nl 合规率</MetricTip>}
					value={pctText(o.dim_pass.nl)}
					hint={
						o.nl_assertions?.not_applicable ? (
							<span data-testid="plan-report-nl-na">
								{`不适用 ${o.nl_assertions.not_applicable} 条，不进分母（EV-23）`}
							</span>
						) : undefined
					}
				/>
				<KpiCard
					label={<MetricTip id="eval.n">有效样本</MetricTip>}
					value={o.n}
					hint={`共 ${o.scenario_count} 题`}
				/>
				<KpiCard
					label={<MetricTip id="eval.undetermined">未判定</MetricTip>}
					value={<span data-testid="plan-report-undetermined">{o.undetermined}</span>}
					tone={o.undetermined ? "warning" : "neutral"}
					hint="不计入通过率与 pass^k"
				/>
				<KpiCard
					label={<MetricTip id="eval.write_object_errors">写对象错误</MetricTip>}
					value={report.safety.write_object_errors}
					tone={report.safety.write_object_errors ? "danger" : "neutral"}
					hint="订单或行项对不上金标"
				/>
				<KpiCard
					label={<MetricTip id="eval.forbidden_commits">不该做的提交</MetricTip>}
					value={<span data-testid="plan-report-forbidden">{report.safety.forbidden_commits}</span>}
					tone={report.safety.forbidden_commits ? "danger" : "neutral"}
				/>
				<KpiCard
					label={<MetricTip id="eval.duplicate_commits">重复提交</MetricTip>}
					value={
						<span data-testid="plan-report-duplicate">{report.safety.duplicate_commits ?? 0}</span>
					}
					tone={report.safety.duplicate_commits ? "danger" : "neutral"}
					hint="同工具同对象提交两次及以上"
				/>
				<KpiCard
					label={<MetricTip id="eval.false_claims">没办成说办成</MetricTip>}
					value={report.safety.false_claims}
					tone={report.safety.false_claims ? "danger" : "neutral"}
				/>
				<KpiCard
					label={<MetricTip id="eval.guidance">指南遵守（观测）</MetricTip>}
					value={
						o.guidance?.assertions ? `${o.guidance.met ?? 0}/${o.guidance.assertions}` : "未判分"
					}
					tone={o.guidance?.trials_violated ? "warning" : "neutral"}
					hint="指南断言逐条判，不进通过率"
				/>
				<KpiCard
					label={<MetricTip id="eval.fabrication">编造事实（观测）</MetricTip>}
					value={
						o.fabrication?.trials
							? `${o.fabrication.violated ?? 0}/${o.fabrication.trials}`
							: "未判分"
					}
					tone={o.fabrication?.violated ? "warning" : "neutral"}
					hint="有编造的试跑数，不进通过率"
				/>
			</div>

			<div className="flex flex-wrap items-center gap-3 text-sm">
				<span className="text-xs text-text-muted">
					<MetricTip id="eval.pass_k">pass^k（跨题平均）</MetricTip>
				</span>
				{Object.entries(o.pass_k).map(([k, v]) => (
					<span key={k}>
						pass^{k} = <b className="text-text-primary">{v}</b>
					</span>
				))}
				{Object.keys(o.pass_k).length ? null : <span className="text-text-muted">—</span>}
				{undet.length ? (
					<span className="text-xs text-text-muted">
						未判定原因：
						{undet.map(([k, n]) => `${zh(UNDETERMINED_ZH, k)} ${n}`).join(" · ")}
					</span>
				) : null}
			</div>

			<PlanTable
				rows={report.by_family}
				rowKey={(f) => f.family}
				columns={FAMILY_COLUMNS}
				density="compact"
			/>
			{ins.length ? (
				<div className="text-xs text-text-muted" data-testid="plan-report-insufficient">
					次数不足（有效次数不够规定次数：多诉求 5、其余 3；不进稳定分类、pass^k 与按族通过率）：
					{ins.map((x) => `${x.scenario_id} ${x.valid}/${x.required}`).join("、")}
				</div>
			) : null}

			<EmotionRiskBlock er={o.emotion_risk} />

			<div className="space-y-3">
				{report.scenarios.map((s) => (
					<div
						key={s.scenario_id}
						className="rounded-lg border border-surface-border"
						data-testid="plan-report-scenario"
					>
						<div className="flex flex-wrap items-center gap-2 border-b border-surface-border px-3 py-2 text-sm">
							<span className="font-medium text-text-primary">{s.scenario_id}</span>
							{s.family ? <Badge tone="muted">{s.family}</Badge> : null}
							{s.sealed ? (
								<span data-testid="plan-report-sealed">
									<Badge tone="neutral">盲测 · 内容不展示</Badge>
								</span>
							) : null}
							<Badge tone={STABLE_TONE[s.stable_class] ?? "muted"}>
								{s.stable_class === "insufficient"
									? `次数不足 ${s.valid}/${s.required}`
									: zh(STABLE_ZH, s.stable_class)}
							</Badge>
							<span className="text-xs text-text-muted">
								通过 {s.results.filter((r) => r.outcome === "pass").length}/{s.valid} · 规定{" "}
								{s.required} 次
								{s.item_completion != null ? (
									<>
										{" · "}
										<MetricTip id="eval.item_completion">事项办成率</MetricTip>{" "}
										{pctText(s.item_completion)}
									</>
								) : null}
							</span>
						</div>
						<div className="divide-y divide-surface-border">
							{s.results.map((r) => (
								<div
									key={r.trial_id}
									className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
									data-testid="plan-report-trial"
								>
									<span className="w-8 text-xs text-text-muted">#{(r.index ?? 0) + 1}</span>
									<OutcomeBadge r={r} />
									<span className="text-xs text-text-muted">
										{zh(PERSONA_ZH, r.persona)} ·{" "}
										{r.termination_reason ? zh(TERMINATION_ZH, r.termination_reason) : "—"} ·{" "}
										{r.num_turns ?? "?"} 轮{r.attempt ? ` · 补跑第 ${r.attempt} 次` : ""}
									</span>
									<DimMark label="action" v={r.dims?.action} />
									<DimMark label="comm" v={r.dims?.communicate} />
									<DimMark label="nl" v={r.dims?.nl} />
									{r.write_object_errors ? (
										<Badge tone="danger">写对象错误 {r.write_object_errors}</Badge>
									) : null}
									{r.forbidden_commits ? (
										<Badge tone="danger">不该做的提交 {r.forbidden_commits}</Badge>
									) : null}
									{r.duplicate_commits ? (
										<Badge tone="danger">重复提交 {r.duplicate_commits}</Badge>
									) : null}
									{r.false_claims ? <Badge tone="danger">没办成说办成</Badge> : null}
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="ml-auto"
										onClick={() => onDrill(r, s.scenario_id)}
										data-testid="plan-report-drill"
									>
										查看详情
									</Button>
								</div>
							))}
						</div>
					</div>
				))}
				{report.scenarios.length ? null : (
					<EmptyState title="这批还没有结果" desc="跑完第一次试跑后，这里按题列出每次的结论。" />
				)}
			</div>
		</div>
	);
}

/* ───────────────────────────── 一次试跑：轨迹抽屉 ＋ 评分卡条 ───────────────────────────── */

function Scorecard({ t }: { t: PlanTrialDetail }) {
	const card = t.scorecard.card;
	const nl = card?.nl?.results ?? [];
	const nlJudged = nl.filter((r) => !r.na);
	const det = card?.deterministic?.results ?? [];
	const items = t.scorecard.items.filter((i) => i.activated);
	return (
		<div
			className="space-y-2 border-b border-surface-border px-4 py-3 text-sm"
			data-testid="plan-trial-scorecard"
		>
			<div className="flex flex-wrap items-center gap-2">
				<OutcomeBadge r={{ outcome: t.outcome, reason: t.undetermined_reason }} />
				<DimMark label="action" v={card?.dims?.action} />
				<DimMark label="communicate" v={card?.dims?.communicate} />
				<DimMark label="nl" v={card?.dims?.nl} />
				{(card?.failed_dims ?? []).length ? (
					<Badge tone="danger">失败维 {(card?.failed_dims ?? []).join("、")}</Badge>
				) : null}
				<span className="text-xs text-text-muted">
					性格 {zh(PERSONA_ZH, t.persona)} · {t.session_output.num_turns ?? "?"} 轮 ·{" "}
					{t.session_output.termination_reason
						? zh(TERMINATION_ZH, t.session_output.termination_reason)
						: "—"}
				</span>
				<Badge tone={t.scorecard.derived_terminal.write_object_errors ? "danger" : "muted"}>
					写对象错误 {t.scorecard.derived_terminal.write_object_errors ?? 0}
				</Badge>
				<Badge tone={t.scorecard.derived_terminal.forbidden_commits ? "danger" : "muted"}>
					不该做的提交 {t.scorecard.derived_terminal.forbidden_commits ?? 0}
				</Badge>
				<Badge tone={t.scorecard.derived_terminal.duplicate_commits ? "danger" : "muted"}>
					重复提交 {t.scorecard.derived_terminal.duplicate_commits ?? 0}
				</Badge>
				<Badge tone={t.session_output.false_claims ? "danger" : "muted"}>
					没办成说办成 {t.session_output.false_claims ?? 0}
				</Badge>
			</div>
			{t.detail && t.outcome === "undetermined" ? (
				<div className="text-xs text-text-secondary">{t.detail}</div>
			) : null}
			{items.length ? (
				<div className="flex flex-wrap items-center gap-1.5 text-xs">
					<span className="text-text-muted">事项</span>
					{items.map((i) => (
						<Badge key={i.gold_item_id} tone={i.pass ? "success" : "danger"}>
							{i.gold_item_id} 预期{i.outcome_expected ?? "—"} → 实际{i.outcome_actual ?? "—"}
						</Badge>
					))}
				</div>
			) : null}
			{det.length ? (
				<details className="text-xs" data-testid="plan-trial-deterministic">
					<summary className="cursor-pointer text-text-secondary">
						确定性检查（{det.filter((r) => r.dim && r.met).length}/{det.filter((r) => r.dim).length}{" "}
						条通过{det.some((r) => !r.dim) ? `，${det.filter((r) => !r.dim).length} 条未机判` : ""}
						；并进动作 ／ 告知维，EV-25）
					</summary>
					<ul className="mt-1.5 space-y-1">
						{det.map((r) => (
							<li key={r.check}>
								<Badge tone={!r.dim ? "muted" : r.met ? "success" : "danger"}>
									{!r.dim ? "未机判" : r.met ? "✓" : "✗"}
								</Badge>{" "}
								<span className="text-text-secondary">{r.check}</span>
								{r.detail ? <span className="text-text-muted"> — {r.detail}</span> : null}
							</li>
						))}
					</ul>
				</details>
			) : null}
			{nl.length ? (
				<details className="text-xs">
					<summary className="flex cursor-pointer items-center gap-1.5 text-text-secondary">
						<AiMark /> 判官逐条结论（{nlJudged.filter((r) => r.met).length}/{nlJudged.length} 条满足
						{nl.length > nlJudged.length ? `，${nl.length - nlJudged.length} 条不适用` : ""}）
					</summary>
					<ul className="mt-1.5 space-y-1">
						{nl.map((r) => (
							<li key={r.assertion}>
								<Badge
									tone={r.na ? "muted" : r.met ? "success" : r.met === false ? "danger" : "muted"}
								>
									{r.na ? "不适用" : r.met ? "✓" : r.met === false ? "✗" : "—"}
								</Badge>{" "}
								<span className="text-text-secondary">{r.assertion}</span>
								{r.reason ? <span className="text-text-muted"> — {r.reason}</span> : null}
							</li>
						))}
					</ul>
				</details>
			) : null}
		</div>
	);
}

/** 点一次试跑：有运行记录 → 运行轨迹抽屉（单元 3）＋ 顶部评分卡条；没跑起来 → 抽屉里写原因 */
export function TrialDrawer({
	trialId,
	label,
	onClose,
}: {
	trialId: string | null;
	label?: string;
	onClose: () => void;
}) {
	const q = usePlanTrial(trialId);
	if (!trialId) return null;
	const t = q.data;
	const recordId = t?.record_ids.length ? t.record_ids[t.record_ids.length - 1] : null;
	if (t?.sealed)
		return (
			<Drawer
				open
				onClose={onClose}
				title={label ?? t.scenario_id}
				testId="plan-trial-drawer"
				flush
				width="min(760px,95vw)"
			>
				<div
					className="flex flex-wrap items-center gap-2 border-b border-surface-border px-4 py-3 text-sm"
					data-testid="plan-trial-sealed-head"
				>
					<OutcomeBadge r={{ outcome: t.outcome, reason: t.undetermined_reason }} />
					<span className="text-xs text-text-muted">
						{t.session_output.num_turns ?? "?"} 轮 ·{" "}
						{t.session_output.termination_reason
							? zh(TERMINATION_ZH, t.session_output.termination_reason)
							: "—"}{" "}
						· 花费 {usd(t.cost_usd)}
					</span>
				</div>
				<SealedNotice />
			</Drawer>
		);
	if (t && recordId)
		return (
			<PlanTraceDrawer
				recordId={recordId}
				onClose={onClose}
				titlePrefix={label}
				topStrip={<Scorecard t={t} />}
			/>
		);
	return (
		<Drawer
			open
			onClose={onClose}
			title={label ?? "一次试跑"}
			testId="plan-trial-drawer"
			flush
			width="min(760px,95vw)"
		>
			{q.isLoading ? (
				<div className="p-4">
					<LoadingInline text="读取这次试跑…" />
				</div>
			) : q.isError || !t ? (
				<FailState
					title="没取到这次试跑"
					reason={planErrorMessage(q.error)}
					onRetry={() => q.refetch()}
					retrying={q.isFetching}
				/>
			) : (
				<>
					<Scorecard t={t} />
					<EmptyState
						title="这次没跑起来"
						desc={`没有会话记录（${t.undetermined_reason ? zh(UNDETERMINED_ZH, t.undetermined_reason) : zh(OUTCOME_ZH, t.outcome)}）；补跑后再看轨迹。`}
					/>
				</>
			)}
		</Drawer>
	);
}

/** 报告页签内容：进度块 ＋ 报告（跑批中每 4 秒刷新一次已完成部分；跑完再补拉一次最终报告） */
export function BatchRunView({ batchId }: { batchId: string }) {
	const progress = useBatchProgress(batchId);
	const status = progress.data?.status ?? "";
	const live = BATCH_ACTIVE.has(status);
	const report = usePlanBatchReport(batchId, live);
	// 进度已到终态、手上的报告还是跑批中那份（partial）→ 补拉一次最终报告。
	// 轮询在终态时就停了，不补拉会停在「次数不足 ／ 待跑」的样子（PR9 线上验收缺陷 2）。每段跑完只补一次，防止后端口径不一致时来回拉。
	const finalFetched = useRef<string | null>(null);
	const { refetch } = report;
	const stalePartial = !!report.data?.partial;
	useEffect(() => {
		if (live) {
			finalFetched.current = null; // 补跑后又跑起来：下次终态还要再补拉
			return;
		}
		if (!status || !stalePartial) return;
		const key = `${batchId}:${status}`;
		if (finalFetched.current === key) return;
		finalFetched.current = key;
		void refetch();
	}, [batchId, status, live, stalePartial, refetch]);
	const [drill, setDrill] = useState<{ id: string; label: string } | null>(null);
	return (
		<>
			<BatchProgressBlock
				batchId={batchId}
				provider={
					report.data ? report.data.header.run_env?.llm_provider || "openrouter" : undefined
				}
			/>
			<Card data-testid="plan-block-report">
				<CardContent className="p-4">
					{report.isLoading ? (
						<LoadingInline text="加载批次报告…" />
					) : report.isError || !report.data ? (
						<FailState
							title="批次报告没加载出来"
							reason={report.error ? planErrorMessage(report.error) : "接口没有返回"}
							onRetry={() => report.refetch()}
							retrying={report.isFetching}
						/>
					) : (
						<BatchReportBody
							report={report.data}
							onDrill={(r, sid) =>
								setDrill({ id: r.trial_id, label: `${sid} · 第 ${(r.index ?? 0) + 1} 次` })
							}
						/>
					)}
				</CardContent>
			</Card>
			<TrialDrawer
				trialId={drill?.id ?? null}
				label={drill?.label}
				onClose={() => setDrill(null)}
			/>
		</>
	);
}
